// tools-dropdown.js - Logik-Popup-Schliessen, Tools-Dropdown, Format-Modus und Ausfuehren gewaehlter Tools (DOMContentLoaded)

import { diffView } from "../diff-view.js";
import { showLogicCheckPopup } from "./logic-check-popup.js";

// Initialize logic check popup close button
document.addEventListener('DOMContentLoaded', () => {
  // Initialize diff view
  diffView.init();

  const closeBtn = document.getElementById('logic-check-popup-close');
  const popup = document.getElementById('logic-check-popup');
  const editorPanel = document.getElementById('markdown-editor');

  if (closeBtn && popup) {
    closeBtn.addEventListener('click', () => {
      popup.classList.remove('visible');
      // Remove split-screen class from editor panel
      if (editorPanel) {
        editorPanel.classList.remove('split-with-logic-check');
      }
    });

    // Close on clicking outside the popup content
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        popup.classList.remove('visible');
        // Remove split-screen class from editor panel
        if (editorPanel) {
          editorPanel.classList.remove('split-with-logic-check');
        }
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('visible')) {
        popup.classList.remove('visible');
      }
    });
  }

  // ========================================
  // Toolbar Dropdown Menu
  // ========================================
  const dropdownBtn = document.getElementById('btn-tools-dropdown');
  const dropdownMenu = document.getElementById('tools-dropdown-menu');

  if (dropdownBtn && dropdownMenu) {
    // Toggle dropdown on button click
    dropdownBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isVisible = dropdownMenu.style.display === 'block';
      dropdownMenu.style.display = isVisible ? 'none' : 'block';
      // Toggle class to hide tooltip when dropdown is open
      dropdownBtn.classList.toggle('dropdown-open', !isVisible);
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!dropdownMenu.contains(e.target) && e.target !== dropdownBtn) {
        dropdownMenu.style.display = 'none';
        dropdownBtn.classList.remove('dropdown-open');
      }
    });

    // Store selected tools - load from localStorage
    const loadSelectedTools = () => {
      try {
        const saved = localStorage.getItem('selected-tools');
        return saved ? new Set(JSON.parse(saved)) : new Set();
      } catch (error) {
        console.error('Failed to load selected tools:', error);
        return new Set();
      }
    };

    const saveSelectedTools = () => {
      try {
        localStorage.setItem('selected-tools', JSON.stringify([...selectedTools]));
      } catch (error) {
        console.error('Failed to save selected tools:', error);
      }
    };

    const selectedTools = loadSelectedTools();

    // Function to update the tools counter badge
    const updateToolsCounter = () => {
      const badge = document.getElementById('tools-counter-badge');
      if (badge) {
        const count = selectedTools.size;
        badge.textContent = count;
        badge.style.display = count > 0 ? 'inline-flex' : 'none';
      }
    };

    // Toggle tool selection in dropdown
    const dropdownItems = document.querySelectorAll('.dropdown-item[data-tool]');
    dropdownItems.forEach(item => {
      const tool = item.dataset.tool;

      // Restore saved selection state
      if (selectedTools.has(tool)) {
        item.classList.add('active');
      }

      item.addEventListener('click', (e) => {
        e.stopPropagation();

        if (selectedTools.has(tool)) {
          selectedTools.delete(tool);
          item.classList.remove('active');
        } else {
          selectedTools.add(tool);
          item.classList.add('active');
        }

        // Save to localStorage and update counter badge
        saveSelectedTools();
        updateToolsCounter();
      });
    });

    // "templates" gibt es nicht mehr: eine alte Auswahl aus dem Browserspeicher
    // wuerde sonst den Zaehler verfaelschen.
    if (selectedTools.delete('templates')) saveSelectedTools();

    // Format war vom 12.09.2026 an fest eingeschaltet und stand nicht im
    // Dropdown; der Stand von damals hat "format" aus dem Speicher geloescht.
    // Einmalig wieder einschalten, damit niemand Format unbemerkt verliert.
    try {
      if (!localStorage.getItem('format-wieder-waehlbar')) {
        if (!selectedTools.has('format')) {
          selectedTools.add('format');
          document.getElementById('btn-format-check-dropdown')?.classList.add('active');
          saveSelectedTools();
        }
        localStorage.setItem('format-wieder-waehlbar', '1');
      }
    } catch (error) {
      console.error('Failed to migrate format selection:', error);
    }

    // Initialize counter badge with saved state
    updateToolsCounter();

    // Execute selected tools button
    const executeBtn = document.getElementById('btn-execute-selected');
    if (executeBtn) {
      executeBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        if (selectedTools.size === 0) {
          alert('Bitte wählen Sie zuerst Tools aus dem Dropdown aus.');
          return;
        }

        // Guard: ohne das Global aus setup.js (initEditor) waere jeder nackte
        // editorAPI-Zugriff in diesem Handler ein ReferenceError.
        if (!window.editorAPI) {
          alert('Der Editor ist noch nicht bereit.');
          return;
        }

        const text = editorAPI.getText();
        if (!text || text.trim().length === 0) {
          alert('Bitte geben Sie zuerst Text ein.');
          return;
        }

        // Capture original text BEFORE tool execution for diff view
        diffView.captureOriginal(text);

        // Disable button during processing
        executeBtn.disabled = true;
        executeBtn.style.opacity = '0.5';

        try {
          // Reihenfolge: Grammatik, Format, Logik — jeweils nur, wenn gewaehlt.
          const ablauf = ['grammar', 'format', 'logic'].filter(
            schritt => selectedTools.has(schritt)
          );

          // Bricht der Format-Schritt ab, endet der Durchgang — der Diff wird
          // trotzdem berechnet, sonst saehe die Benutzerin nicht, was die
          // Grammatik davor schon geaendert hat.
          let abbruch = false;

          for (const tool of ablauf) {
            console.log(`Executing tool: ${tool}`);

            switch (tool) {
              case 'grammar':
                await editorAPI.checkGrammar();
                break;

              case 'format': {
                // Format strukturiert nach dem eingebauten Standardskelett.
                const formatResponse = await fetch('/api/mcp/tools/call', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    tool_name: 'basic_format',
                    params: { report_text: editorAPI.getText() },
                  }),
                });
                const formatResult = await formatResponse.json();
                if (!formatResult.success) {
                  // Kein stiller Rueckfall: der Text bleibt unangetastet.
                  alert(`Formatierung fehlgeschlagen: ${formatResult.error || 'unbekannter Fehler'}`);
                  abbruch = true;
                  break;
                }
                editorAPI.setText(formatResult.formatted_text);
                break;
              }

              case 'logic': {
                const response = await fetch('/api/mcp/tools/call', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    tool_name: 'ultrathink',
                    params: { text: editorAPI.getText() }
                  })
                });

                if (response.ok) {
                  const result = await response.json();
                  showLogicCheckPopup(result);
                }
                break;
              }
            }

            if (abbruch) break;
          }

          // Nach ALLEN Tools: Diff berechnen, aber nicht oeffnen. Sichtbar
          // wird er nur ueber den Diff-Knopf.
          diffView.prepareDiff(editorAPI.getText());

        } catch (error) {
          console.error('Error executing tools:', error);
          alert(`Fehler bei der Ausführung: ${error.message}`);
        } finally {
          executeBtn.disabled = false;
          executeBtn.style.opacity = '1';
          editorAPI.view.focus();
        }
      });
    }
  }
});
